import { Slice } from "@reduxjs/toolkit";

import { EntityFormState, FormLens, Pathable, PathSelector, PathsSelectorFrom, useFormEdits, useZoom } from "@tdp/common/form";

import { createFormLensHook } from "./createFormLensHook";
import { createFormFieldPropHook } from "./createFormPropFactory";
import { liftLensHook } from "./liftLensHook";
import { useFormInputProps } from "./useFormInputProps";
import { useFormRevert } from "./useFormRevert";

/**
 * Utility function for creating hook factories for a given form entity "slice" (passed in via the lensFn or selectById function)
 * @param lensFn A function for getting a lens for a specific entity by its id.
 */
export function createFormHookFactories<TEntity extends Pathable>(lensFn: (id: string) => FormLens<TEntity>) {
  return {
    // Generic 'zoom' for a given property / sub-object. 
    // This is an escape-hatch if the prop hooks don't cover the use case(s).
    createZoomHook: <TLeaf>(pathFn: PathSelector<TEntity, TLeaf>) => (id: string) => useZoom(lensFn(id), pathFn),

    // For tracking one or more (potential) edits to an entity w/o tracking the whole thing.
    createEditsHook: (pathsSelector: PathsSelectorFrom<TEntity>) => (id: string) => useFormEdits(lensFn(id), pathsSelector),

    // For reverting one or more (potential) edits to an entity w/o reverting the whole thing.
    createRevertHook: (pathsSelector: PathsSelectorFrom<TEntity>) => (id: string) => useFormRevert(lensFn(id), pathsSelector),

    // Hooks for generating props for input components
    createStringInputPropsHook: liftLensHook(lensFn, createFormFieldPropHook(useFormInputProps)),
  }
}

// Utility for skipping the selector for the base entity lens and deriving directly from the slice
export function createSliceHookFactories<TEntity extends Pathable>(
  slice: Pick<Slice<EntityFormState<TEntity>>, 'actions' | 'getSelectors' | 'selectSlice'>
) {
  return createFormHookFactories(createFormLensHook(slice));
}
